import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import {
  adminActions,
  adminGetOtherProfessionalContract,
  convertToDecimals,
  currency,
  getOtherProfessionalsRequests,
  otherContractOffers,
  otherProfessionalContract,
  pharmacyActions,
} from '@pharmaplan/common';
import { ReactComponent as StarFilled } from '@pharmaplan/common/assets/icons/starFilled.svg';

import CustomAvatarSection from '../../common/CustomAvatarSection';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

import { LocationText } from '../../Admin/AvailableWorkshifts/AvailableWorkshifts';
import getLongTermContractLabels from '../../common/LongTermContractLabels/LongTermContractLabels';
import genericClasses from '../../../theme/GenericClasses';
import CustomButton from '../../common/CustomButton';
import strings from '../../../localization';
import { Constants, ScreenTypes } from '../../../helpers/Constants';
import { resetDialog } from '../../../reducers/dialogReducer';

import styles from './styles';
import WithSkeleton from '../../common/WithSkeleton';
import { getDayMonthDateYear } from '../../Reports/helpers';
import useAdmin from '../../../hooks/useAdmin';
import { renderScreen, setOpen } from '../../../actions/drawerActions';

interface IViewOtherRequestedOffers {
  workshiftId: string;
  professionalIds?: Array<string>;
  longTermId?: string;
}

const ViewOtherRequestedOffers: FC<IViewOtherRequestedOffers> = ({
  workshiftId,
  professionalIds,
  longTermId,
}) => {
  const dispatch = useAppDispatch();

  const { isAdmin } = useAdmin();
  const successAction = isAdmin ? adminActions.getOtherProfessionalContracts : pharmacyActions.getOtherProfessionalsRequests;

  const pharmacyOffers = useAppSelector(otherContractOffers);
  const adminOffers = useAppSelector(otherProfessionalContract);

  const offers = isAdmin ? adminOffers : pharmacyOffers;

  useEffect(() => {
    if (isAdmin) {
      dispatch(
        adminGetOtherProfessionalContract({
          workshiftId,
          showOther: !!longTermId,
        }),
      );
    } else {
      dispatch(getOtherProfessionalsRequests(workshiftId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    dispatch(resetDialog());
  };

  const goToRequestedPharmacist = (professionalId:string, bookingId:string) =>
    () => {
      dispatch(resetDialog());
      dispatch(setOpen(true));
      dispatch(renderScreen({
        screenNumber: 3,
        screenType: ScreenTypes.requestedList,
        eventId: bookingId,
        workshiftId,
        extraParameters: {
          longTermShiftContractId: longTermId,
        },
        pharmacistId: professionalId,

      }));
    };

  const SkeletonTypography = WithSkeleton({ successAction, variant: 'text' })(
    Typography,
  );

  const title = `${getDayMonthDateYear(offers?.[0]?.contractStartDate)} ${
    strings.to
  } ${getDayMonthDateYear(offers?.[0]?.contractEndDate)}`;

  const RightSection: FC<{
    distance: number;
    price: number;
    rating: number;
  }> = useCallback(
    ({ rating, distance, price }) =>
      (
        <Stack>
          <Box sx={[styles.ratingContainer, styles.rightSectionMargin]}>
            <StarFilled height={11} width={11} />
            <SkeletonTypography sx={styles.ratingText}>
              {rating?.toPrecision(2)}
            </SkeletonTypography>
          </Box>
          <SkeletonTypography
            sx={[styles.boldText, styles.rightSectionMargin, styles.rightAlign]}
          >
            {`${currency}${price}`}
          </SkeletonTypography>
          <SkeletonTypography
            sx={[styles.distance, styles.rightSectionMargin, styles.rightAlign]}
          >
            {`${strings.formatString(strings.numAway, distance)}`}
          </SkeletonTypography>
        </Stack>
      ),
    [],
  );

  const totalOffers = useMemo(
    () => {
      if (professionalIds) {
        return offers?.filter((item) =>
          !professionalIds?.includes(item.professionalId));
      }
      return offers;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [offers],
  );

  return (
    <Box sx={styles.container}>
      <Box sx={styles.subContainer}>
        <SkeletonTypography sx={[styles.title, styles.boldText]}>
          {title}
        </SkeletonTypography>
        {!totalOffers?.length && (
          <Box sx={styles.emptyContainer}>{strings.noRequests}</Box>
        )}
        <Box sx={styles.grid}>
          {totalOffers.map((item) => {
            const {
              bookingId,
              firstName,
              professionalId,
              lastName,
              image,
              price,
              distance,
              requestCount,
              city,
              rating,
              requestEndDate,
              counterPrice,
              requestStartDate,
            } = item ?? {};
            const name = `${firstName} ${lastName}`;
            const calcRating = (rating * 5) / 100;
            const { RangeLabel } = getLongTermContractLabels({
              successAction,
              contractEndDate: requestEndDate,
              contractStartDate: requestStartDate,
              isLongTerm: true,
            });
            return (
              <CustomAvatarSection
                key={bookingId}
                onClick={goToRequestedPharmacist(professionalId, bookingId)}
                customTitleStyle={styles.name}
                imageSuccessAction={successAction}
                avatar={image}
                config={[
                  {
                    key: '1',
                    value: strings.formatString(
                      strings.numberRequests,
                      requestCount,
                    ),
                  },
                  { key: '2', value: <RangeLabel sx={{ my: 0 }} /> },
                  { key: '3', value: <LocationText label={city} /> },
                ]}
                title={name}
                menuActions={null}
                successAction={successAction}
                RightSection={(
                  <RightSection
                    price={convertToDecimals(counterPrice || price) as number}
                    distance={distance}
                    rating={calcRating}
                  />
                )}
              />
            );
          })}
        </Box>
      </Box>
      <Box sx={[styles.buttonContainer, genericClasses.flex]}>
        <CustomButton
          customButtonStyle={styles.closeButton}
          label={strings.close}
          onClick={handleClose}
          variant={Constants.variant.contained}
        />
      </Box>
    </Box>
  );
};

export default ViewOtherRequestedOffers;
